import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { fetchSocialAccounts, getSocialAccountAuthUrl, createSocialAccount } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Plus,
  LinkIcon,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";
import { PLATFORM_MAP } from "@/lib/constants";
import { Platform } from "@/types";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { PageShell } from "@/components/page-shell";

const accountsSearchSchema = z.object({
  connected: z.string().optional(),
  platform: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
});

export const Route = createFileRoute("/accounts/")({
  component: AccountsPage,
  validateSearch: accountsSearchSchema,
});

function AccountsPage() {
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [oauthMessage, setOauthMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [blueskyOpen, setBlueskyOpen] = useState(false);
  const [blueskyHandle, setBlueskyHandle] = useState("");
  const [blueskyPassword, setBlueskyPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [blueskyLoading, setBlueskyLoading] = useState(false);
  const [blueskyError, setBlueskyError] = useState<string | null>(null);
  const navigate = useNavigate({ from: "/accounts/" });

  const search = useSearch({ from: "/accounts/" });
  const connected = search.connected;
  const errorParam = search.error;
  const platformParam = search.platform;

  useEffect(() => {
    if (connected) {
      setOauthMessage({ type: "success", text: `Connected ${platformParam ?? ""} account`.trim() });
      navigate({ to: "/accounts", search: {} });
    } else if (errorParam) {
      setOauthMessage({ type: "error", text: `Connection failed: ${errorParam}` });
      navigate({ to: "/accounts", search: {} });
    }
  }, [connected, errorParam, platformParam, navigate]);

  useEffect(() => {
    if (!oauthMessage) return;
    const t = setTimeout(() => setOauthMessage(null), 5000);
    return () => clearTimeout(t);
  }, [oauthMessage]);

  const {
    data: accounts,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["social-accounts"],
    queryFn: () => fetchSocialAccounts(),
    // Fallback polling: refresh every 60s since we don't have webhooks yet
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    // Refresh when user returns to the tab
    refetchOnWindowFocus: true,
    // Data considered fresh for 30s
    staleTime: 30_000,
  });

  const handleConnect = async (platform: string) => {
    if (platform === "bluesky") {
      setBlueskyOpen(true);
      return;
    }
    setConnectingPlatform(platform);
    try {
      const resp = await getSocialAccountAuthUrl(
        platform,
        platform === "instagram" ? "facebook" : undefined,
      );
      window.open(resp.url, "_blank");
    } catch (e) {
      alert(String(e));
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleBlueskySubmit = async () => {
    setBlueskyError(null);
    if (!blueskyHandle.trim() || !blueskyPassword.trim()) {
      setBlueskyError("Handle and app password are required");
      return;
    }
    setBlueskyLoading(true);
    try {
      await createSocialAccount({
        platform: "bluesky",
        access_token: blueskyPassword.trim(),
        user_id: blueskyHandle.trim(),
        platform_data: {
          bluesky: {
            app_password: blueskyPassword.trim(),
            handle: blueskyHandle.trim(),
          },
        },
      });
      setBlueskyOpen(false);
      setBlueskyHandle("");
      setBlueskyPassword("");
      setOauthMessage({ type: "success", text: "Connected Bluesky account" });
      refetch();
    } catch (e) {
      setBlueskyError(String(e));
    } finally {
      setBlueskyLoading(false);
    }
  };

  const activeCount = accounts?.filter((a) => a.status === "connected").length ?? 0;
  const totalCount = accounts?.length ?? 0;

  return (
    <PageShell title="Accounts" maxWidth="xl">
      {/* ── Toast ── */}
      {oauthMessage && (
        <div
          className={cn(
            "mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm shadow-sm animate-in slide-in-from-top-2",
            oauthMessage.type === "success"
              ? "bg-emerald-50/80 text-emerald-700 border border-emerald-100"
              : "bg-red-50/80 text-red-700 border border-red-100",
          )}
        >
          {oauthMessage.type === "success" ? (
            <CheckCircle2 className="w-4 h-4 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0" />
          )}
          {oauthMessage.text}
        </div>
      )}

      {isLoading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState error={String(error)} onRetry={refetch} />
      ) : totalCount === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* ── Summary bar ── */}
          <div className="flex items-baseline justify-between mb-8">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold tracking-tight">{totalCount}</span>
              <span className="text-sm text-muted-foreground">
                account{totalCount !== 1 ? "s" : ""}
                <span className="mx-1.5 text-border">·</span>
                <span className="text-emerald-600 font-medium">{activeCount}</span> active
              </span>
            </div>
            <button
              onClick={() => refetch()}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
          </div>

          {/* ── Account list ── */}
          <div className="space-y-1">
            {accounts!.map((acc) => {
              const config = PLATFORM_MAP[acc.platform as Platform];
              const Icon = config?.icon ?? LinkIcon;
              const color = config?.color ?? "text-slate-500";
              const isActive = acc.status === "connected";

              return (
                <div
                  key={acc.id}
                  className="group flex items-center gap-4 py-3.5 px-2 -mx-2 rounded-xl hover:bg-muted/40 transition-all duration-200 cursor-default"
                >
                  {/* Avatar with gradient ring */}
                  <div className="relative shrink-0">
                    <div
                      className={cn(
                        "w-11 h-11 rounded-full p-[2px] transition-transform duration-300 group-hover:scale-105",
                        isActive
                          ? "bg-gradient-to-tr from-emerald-400 via-teal-400 to-cyan-400"
                          : "bg-muted",
                      )}
                    >
                      <div className="w-full h-full rounded-full bg-background p-[2px]">
                        {acc.profilePhotoUrl ? (
                          <img
                            src={acc.profilePhotoUrl}
                            alt=""
                            referrerPolicy="no-referrer"
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full rounded-full bg-muted flex items-center justify-center">
                            <Icon className={cn("w-4 h-4", color)} />
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Status dot */}
                    <div
                      className={cn(
                        "absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background",
                        isActive ? "bg-emerald-500" : "bg-amber-400",
                      )}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[15px] font-medium leading-tight truncate">
                        {acc.username ?? acc.userId}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground capitalize">
                        {acc.platform}
                      </span>
                      <span className="text-[10px] text-border">·</span>
                      <span className="text-[11px] text-muted-foreground/60 font-mono truncate max-w-[120px]">
                        {acc.id.slice(0, 12)}…
                      </span>
                    </div>
                  </div>

                  {/* Platform color indicator */}
                  <div
                    className={cn(
                      "w-1.5 h-1.5 rounded-full shrink-0",
                      color.replace("text-", "bg-"),
                    )}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Add Account ── */}
      <section className="mt-10">
        <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-4">
          Connect Platform
        </h3>
        <div className="flex flex-wrap gap-2">
          {[
            "facebook",
            "instagram",
            "linkedin",
            "tiktok",
            "youtube",
            "pinterest",
            "bluesky",
            "threads",
          ].map((platform) => {
            const config = PLATFORM_MAP[platform as Platform];
            const Icon = config?.icon ?? LinkIcon;
            const color = config?.color ?? "text-slate-500";
            const isConnecting = connectingPlatform === platform;
            return (
              <button
                key={platform}
                onClick={() => handleConnect(platform)}
                disabled={isConnecting}
                className={cn(
                  "flex items-center gap-2 pl-3 pr-2.5 py-2 rounded-lg border text-sm transition-all duration-200",
                  isConnecting
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:shadow-md hover:-translate-y-0.5 hover:border-muted-foreground/20 bg-card",
                )}
              >
                <Icon className={cn("w-4 h-4", color)} />
                <span className="capitalize font-medium text-sm">{platform}</span>
                <Plus className="w-3.5 h-3.5 text-muted-foreground/50 ml-0.5" />
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Bluesky Connect Modal ── */}
      {blueskyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setBlueskyOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-card border rounded-2xl shadow-xl p-6 animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">Connect Bluesky</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Enter your Bluesky handle and an app password.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                  Handle
                </label>
                <input
                  type="text"
                  value={blueskyHandle}
                  onChange={(e) => setBlueskyHandle(e.target.value)}
                  placeholder="username.bsky.social"
                  className="w-full h-10 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-shadow"
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                  App Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={blueskyPassword}
                    onChange={(e) => setBlueskyPassword(e.target.value)}
                    placeholder="abcd-efgh-ijkl-mnop"
                    className="w-full h-10 px-3 pr-10 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-shadow"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground/70 mt-1.5">
                  Generate one in{" "}
                  <a
                    href="https://bsky.app/settings/app-passwords"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Bluesky Settings → App Passwords
                  </a>
                </p>
              </div>

              {blueskyError && (
                <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {blueskyError}
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setBlueskyOpen(false)}
                disabled={blueskyLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleBlueskySubmit}
                disabled={blueskyLoading || !blueskyHandle.trim() || !blueskyPassword.trim()}
              >
                {blueskyLoading ? "Connecting…" : "Connect"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

/* ── Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-1 animate-pulse">
      <div className="h-9 w-20 bg-muted rounded-lg mb-8" />
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 py-3.5 px-2">
          <div className="w-11 h-11 rounded-full bg-muted shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="w-28 h-3.5 bg-muted rounded" />
            <div className="w-20 h-2.5 bg-muted rounded" />
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-muted" />
        </div>
      ))}
    </div>
  );
}

/* ── Error ── */
function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
      <XCircle className="w-8 h-8 text-red-400" />
      <div className="text-sm text-muted-foreground">{error}</div>
      <Button onClick={onRetry} size="sm" variant="outline">
        <RefreshCw className="w-3.5 h-3.5 mr-1" />
        Retry
      </Button>
    </div>
  );
}

/* ── Empty ── */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-50 flex items-center justify-center">
        <LinkIcon className="w-6 h-6 text-orange-400" />
      </div>
      <div>
        <div className="text-base font-medium">No accounts yet</div>
        <div className="text-sm text-muted-foreground mt-0.5">
          Connect your first social platform
        </div>
      </div>
    </div>
  );
}
